import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { LinearProgress } from '@mui/material'
import Radio from '@mui/material/Radio'
import { styled } from '@mui/material/styles'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import ButtonMobileComponent from 'components/button-mobile'
import ModalConfirmComponent from 'components/modal-confirm'
import MsgPanelComponent, { MsgPanelComponentProps } from 'components/msg-panel'
import SelectMobileComponent from 'components/select-mobile'
import ToastComponent from 'components/toast'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import {
  CalendarInputProps,
  CarpoolOptionProps,
  CarpoolState,
  isCalendarTypeCarpoolEventData,
} from 'types/CalendarTypes'
import { MessageProps } from 'types/CommonTypes'
import { CommunityCarpoolOutputProps, DateCarpoolProps, DefaultCarpoolType } from 'types/CommunityTypes'
import { StudentProps } from 'types/StudentTypes'
import { UsersOutputProps } from 'types/UserTypes'
import { getCommunityArrivalTime, getCommunityDismissalTime } from 'utils/calendar'
import { getCommunityCarpools } from 'utils/communities'
import { getTimeNumber, getTimeString, getTimeStringFromNumber } from 'utils/constants'
import { getCommunityUsersSortedByDistance } from 'utils/userUtils'
import './index.css'
dayjs.extend(customParseFormat)

enum CarpoolUpdate {
  // States when user is driving on all selected dates
  UPDATE_TIME = 'Update Time', // When user is driving and wants to update time
  STOP_DRIVING = 'Stop Driving', // When user is driving and wants to stop

  // States when someone else is driving on all selected dates
  LEAVE_CARPOOL = 'Leave carpool', // When user is in someone else's carpool and wants to leave the carpool

  // States for mixed selections
  I_WILL_DRIVE = "I'll drive", // When user is not driving and wants to start
  ASSIGN_DRIVER = 'Assign driver', // When user wants to join a driver's carpool
}

interface CarpoolSummary {
  singleEventDriverId: string
  driverText: string
  timeText: string
  allUserDriving: boolean
  allSomeoneElseDriving: boolean
}

const CustomRadio = styled(Radio)(({ theme }) => ({
  marginLeft: '4px',
  color: '#A3A3A3', // Default color when not checked
  width: '16px',
  '&.Mui-checked': {
    color: '#E5C265', // Color when checked
  },
}))

const formatCarpoolSchedule = (dates: string[], carpools: CommunityCarpoolOutputProps[]) => {
  return dates.map((date) => {
    const carpool = carpools.find((c) => c.carpoolScheduledAt.startsWith(date))
    const dateObj = carpool ? new Date(carpool.carpoolScheduledAt) : new Date(date)

    const formattedDateTime = carpool
      ? new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'UTC',
        }).format(dateObj)
      : new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        }).format(dateObj) + ', No time set'

    return {
      date: formattedDateTime.split(', ')[0],
      time: carpool ? formattedDateTime.split(', ').slice(1).join(', ') : 'No time set',
      fullText: formattedDateTime,
    }
  })
}

// New reusable component for calendar action controls
const CalendarUpdateComponent = ({
  selectedDatesByState,
  selectedEvents,
  carpoolOptions,
  onSave,
  variant = 'default',
}: {
  selectedDatesByState: Record<CarpoolState, string[]>
  selectedEvents: CalendarInputProps[]
  carpoolOptions: CarpoolOptionProps
  onSave: (body: DateCarpoolProps) => void
  variant?: 'default' | 'compact'
}) => {
  // 1. State Hooks
  const navigate: NavigateFunction = useNavigate()
  const prevSelectedDatesByState = useRef(selectedDatesByState)
  const [selectedOption, setSelectedOption] = useState<CarpoolUpdate>()
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null)
  const [selectedDriver, setSelectedDriver] = useState<{
    user: UsersOutputProps
    carpools: CommunityCarpoolOutputProps[]
  }>()
  const [selectedOptionMessage, setSelectedOptionMessage] = useState<MsgPanelComponentProps>()
  const [isShowStopDrivingModal, setIsShowStopDrivingModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<string>('')
  const [msg, setMsg] = useState<MessageProps>()

  // 2. Derived Constants
  const isCompact = variant === 'compact'
  const selectedDates = Object.values(selectedDatesByState).flat().sort()
  const {
    studentId: myStudentId,
    user: { phoneNumber: myUserId },
    userStudents,
    poolType,
    communityUsers,
  } = carpoolOptions
  const student: StudentProps | undefined = userStudents?.find((s) => s.studentId === myStudentId)
  const comId = student?.schoolName

  // Filter out duplicate users and sort by distance & username, filtering out my user id
  const sortedUsers = getCommunityUsersSortedByDistance(communityUsers, myUserId, comId)

  // Get update message panel details
  const getUpdateMessage = useCallback(
    (
      option: CarpoolUpdate,
      driver?: { user: UsersOutputProps; carpools: CommunityCarpoolOutputProps[] },
    ): MsgPanelComponentProps | undefined => {
      if (option === CarpoolUpdate.ASSIGN_DRIVER) {
        if (driver) {
          const missingDates = selectedDates.filter(
            (date) => !driver.carpools.some((c) => c.carpoolScheduledAt.startsWith(date)),
          )
          if (missingDates.length > 0) {
            return {
              style: 'warning',
              heading: `${driver.user.userName.split(' ')[0] || 'User'} is not driving on selected dates`,
              text: (
                <div className='flex flex-col gap-2'>
                  <div>{missingDates.join(', ')}</div>
                  {!isCompact && (
                    <div className='flex justify-center'>
                      <button
                        onClick={() =>
                          navigate(
                            `/userpool/${comId}?studentId=${myStudentId}&userStudentId=${driver.user.userCommunityStudentId}`,
                            { state: { userId: driver.user.userId } },
                          )
                        }
                        className='py-2.5 px-4 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-200'
                      >
                        View User Schedule
                      </button>
                    </div>
                  )}
                </div>
              ),
            }
          } else {
            const formattedSchedules = formatCarpoolSchedule(selectedDates, driver?.carpools || [])
            return {
              style: 'success',
              heading: `${driver.user.userName.split(' ')[0] || 'User'} is driving on selected dates`,
              text: formattedSchedules.map((schedule, index) => (
                <div key={index}>
                  {schedule.fullText}
                  {index < formattedSchedules.length - 1 && <br />}
                </div>
              )),
            }
          }
        } else return undefined
      } else return undefined
    },
    [selectedDates, comId, myStudentId, navigate, isCompact],
  )

  // 3. Effect Hooks
  useEffect(() => {
    if (selectedOption && JSON.stringify(prevSelectedDatesByState.current) !== JSON.stringify(selectedDatesByState)) {
      // Selected driver is reset when dates change. Client needs to retrieve carpools for the udpated dates and driver.
      if (selectedOption === CarpoolUpdate.ASSIGN_DRIVER) setSelectedDriver(undefined)
      setSelectedOptionMessage(getUpdateMessage(selectedOption))
    }
    prevSelectedDatesByState.current = selectedDatesByState
  }, [selectedDatesByState, selectedOption, getUpdateMessage])

  // Dynamic preview panel and CTA
  const carpoolSummary: CarpoolSummary | undefined = useMemo(() => {
    const selectedEventCount = selectedEvents.length
    if (selectedEventCount === 0) return

    // Analyze each date's carpool status
    const summary = selectedEvents.reduce(
      (acc, { eventDate, eventData }) => {
        if (!eventData || !isCalendarTypeCarpoolEventData(eventData)) {
          acc.notCarpoolingDates.push(eventDate)
          return acc
        }

        const { carpoolTime, carpoolDriver } = eventData

        if (carpoolTime >= 0 && carpoolDriver?.userId) {
          const isCurrentUser = carpoolDriver.userId === myUserId
          const timeStr = getTimeString(carpoolTime)

          if (isCurrentUser) {
            acc.userDrivingDates.push({ date: eventDate, time: timeStr })
          } else {
            acc.otherDrivingDates.push({
              date: eventDate,
              time: timeStr,
              driver: carpoolDriver.userName || 'Unknown driver',
              driverId: carpoolDriver.userId,
            })
          }
        } else {
          acc.notCarpoolingDates.push(eventDate)
        }

        return acc
      },
      {
        userDrivingDates: [] as Array<{ date: string; time: string }>,
        otherDrivingDates: [] as Array<{ date: string; time: string; driver: string; driverId: string }>,
        notCarpoolingDates: [] as string[],
      },
    )
    const allUserDriving = summary.userDrivingDates.length === selectedEventCount
    const allSomeoneElseDriving = summary.otherDrivingDates.length === selectedEventCount
    const allDriving = summary.userDrivingDates.length + summary.otherDrivingDates.length === selectedEventCount
    const allNotCarpooling = summary.notCarpoolingDates.length === selectedEventCount

    // Determine the display text for driver and view single carpool event details
    let singleEventDriverId = '',
      driverText = ''
    if (allUserDriving) {
      driverText = 'You'
      singleEventDriverId = selectedEventCount === 1 ? myUserId : ''
    } else if (allSomeoneElseDriving) {
      // Check if all other drivers are the same
      const uniqueDrivers = new Set(summary.otherDrivingDates.map((d) => d.driverId))
      if (uniqueDrivers.size === 1) {
        driverText = summary.otherDrivingDates[0].driver
      } else {
        driverText = 'Multiple drivers'
      }
      singleEventDriverId = selectedEventCount === 1 ? summary.otherDrivingDates[0].driverId : ''
    } else if (allDriving) {
      driverText = 'Multiple drivers'
    } else if (allNotCarpooling) {
      driverText = 'No driver assigned'
    } else {
      driverText = 'Mixed selection'
    }

    // Determine the display text for time
    let timeText = ''
    if (allDriving) {
      const allTimes = [...summary.userDrivingDates, ...summary.otherDrivingDates].map((d) => d.time)
      const uniqueTimes = Array.from(new Set(allTimes))

      if (uniqueTimes.length === 1) {
        timeText = uniqueTimes[0]
      } else if (uniqueTimes.length > 1) {
        timeText = 'Multiple times'
      }
    }

    return {
      singleEventDriverId,
      driverText,
      timeText,
      allUserDriving,
      allSomeoneElseDriving,
    }
  }, [selectedEvents, myUserId])
  const { singleEventDriverId, driverText, timeText, allUserDriving, allSomeoneElseDriving } = carpoolSummary || {}
  const availableOptions = allUserDriving
    ? [CarpoolUpdate.UPDATE_TIME, CarpoolUpdate.STOP_DRIVING]
    : allSomeoneElseDriving
      ? [CarpoolUpdate.LEAVE_CARPOOL]
      : [CarpoolUpdate.I_WILL_DRIVE, CarpoolUpdate.ASSIGN_DRIVER]

  // 4. Derived State, Validations and Early Returns
  if (!comId || !student) return <LinearProgress />
  const { schoolScheduleGroups, scheduleGroupName } = student
  const communityTime =
    poolType === DefaultCarpoolType.ARRIVAL
      ? getCommunityArrivalTime(schoolScheduleGroups, scheduleGroupName)
      : getCommunityDismissalTime(schoolScheduleGroups, scheduleGroupName)
  const defaultTime: Dayjs = dayjs(getTimeStringFromNumber(communityTime ?? 800), 'HH:mm')

  // Validations
  const isValidUpdateTime = selectedOption === CarpoolUpdate.UPDATE_TIME && selectedTime !== null && allUserDriving
  const isValidStopDriving = selectedOption === CarpoolUpdate.STOP_DRIVING && allUserDriving
  const isValidLeaveCarpool = selectedOption === CarpoolUpdate.LEAVE_CARPOOL && allSomeoneElseDriving
  const isValidIWillDrive = selectedOption === CarpoolUpdate.I_WILL_DRIVE && selectedTime !== null
  const isValidAssignDriver =
    selectedOption === CarpoolUpdate.ASSIGN_DRIVER &&
    selectedDriver &&
    selectedDates.filter((date) => !selectedDriver.carpools.some((c) => c.carpoolScheduledAt.startsWith(date)))
      .length === 0
  const isValid =
    isValidUpdateTime || isValidStopDriving || isValidLeaveCarpool || isValidIWillDrive || isValidAssignDriver

  if (isLoading) return <LinearProgress />

  // 5. Event Handlers
  const handleSelectOption = (option: CarpoolUpdate) => {
    setSelectedOption(option)
    setSelectedTime(defaultTime)
    setSelectedDriver(undefined)
    setSelectedOptionMessage(getUpdateMessage(option))
  }
  const handleSelectTime = (time: Dayjs | null) => {
    setSelectedTime(time)
  }
  const handleSelectDriver = async (uid: string) => {
    const startDate =
      selectedDates.length > 0
        ? new Date(Math.min(...selectedDates.map((date) => new Date(date).getTime()))).toISOString().split('T')[0]
        : ''
    const endDate =
      selectedDates.length > 0
        ? new Date(Math.max(...selectedDates.map((date) => new Date(date).getTime()))).toISOString().split('T')[0]
        : ''

    const loadCarpools = async () => {
      const { data } = await getCommunityCarpools(comId, {
        poolType,
        poolDriverId: uid,
        poolStartDate: startDate,
        poolEndDate: endDate,
      })
      return data?.carpools ?? []
    }

    setIsLoading('Loading carpools...')
    const carpools = await loadCarpools()
    const user: UsersOutputProps | undefined = sortedUsers.find((u) => u.userId === uid)
    if (user) {
      const driver = { user, carpools }
      setSelectedDriver(driver)
      setSelectedOptionMessage(getUpdateMessage(CarpoolUpdate.ASSIGN_DRIVER, driver))
      setIsLoading('')
    }
  }

  const handleSubmit = async () => {
    const carpools: DateCarpoolProps = {}
    if (isValidUpdateTime || isValidIWillDrive) {
      // Use the selected time
      const driverId = myUserId
      const time = getTimeNumber(selectedTime.toDate())
      selectedDates.forEach((date) => {
        carpools[date] = { time, driverId }
      })
    } else if (isValidStopDriving || isValidLeaveCarpool) {
      selectedDates.forEach((date) => {
        carpools[date] = { time: -1, driverId: '' }
      })
    } else if (isValidAssignDriver) {
      // Use the driver's carpoolScheduledAt time for each date
      const driverId = selectedDriver.user.userId
      selectedDates.forEach((date) => {
        // Find the carpool for this date
        const carpool = selectedDriver.carpools.find((c) => c.carpoolScheduledAt.startsWith(date))
        // Extract time from carpoolScheduledAt (format: "YYYY-MM-DDTHH:mm:ss.sssZ")
        const timeStr = carpool?.carpoolScheduledAt.split('T')[1]?.split('-')?.[0]
        // If carpool time is not found, show error - should never occur unless driver updated carpool after user selected them
        if (!timeStr) {
          setMsg({
            style: 'error',
            text: `Something went wrong. Please retry or contact support if the issue persists.`,
          })
          return
        }
        const [hours, minutes] = timeStr.split(':').map(Number)
        const time = hours * 100 + minutes
        carpools[date] = { time, driverId }
      })
    }

    if (Object.keys(carpools).length > 0) {
      setIsLoading('Updating carpools...')
      await onSave(carpools)
      setIsLoading('')
    }
  }

  // 6. Render
  return (
    <>
      {msg && <ToastComponent {...msg} onClose={() => setMsg(undefined)} />}
      {!isCompact && (
        // Render the detailed preview when not compact
        <div className='w-full'>
          <div className='flex items-center justify-between w-full'>
            {/* Summary Section */}
            <div className='flex items-center gap-6 sm:gap-8 flex-1 min-w-0'>
              {driverText && (
                <div className='min-w-0'>
                  <div className='flex items-center gap-2'>
                    <h4 className='text-sm font-medium text-gray-900 m-0'>Driver Assignment</h4>
                  </div>
                  <p className='text-sm font-medium text-gray-800 truncate mt-1.5'>{driverText}</p>
                </div>
              )}
              {timeText && (
                <div className='min-w-0'>
                  <div className='flex items-center gap-2'>
                    <h4 className='text-sm font-medium text-gray-900 m-0'>Pickup Time</h4>
                  </div>
                  <p className='text-sm font-medium text-gray-800 truncate mt-1.5'>{timeText}</p>
                </div>
              )}
            </div>

            {/* View More Button */}
            {singleEventDriverId && (
              <button
                onClick={() =>
                  navigate(`/carpool/detail/${poolType}/${selectedDates[0]}?id=${myStudentId}`, {
                    state: { userId: singleEventDriverId, fromPage: 'carpool-management' },
                  })
                }
                className='inline-flex items-center text-sm font-medium text-gray-800 hover:underline shrink-0 ml-4'
                aria-label='View carpool details'
              >
                View more
                <ChevronRightIcon fontSize='small' className='w-5 h-5' />
              </button>
            )}
          </div>
        </div>
      )}

      {/* container below preview */}
      <div className={`flex flex-col ${isCompact ? 'pt-2 space-y-3' : 'border-t border-gray-200 pt-4 space-y-4'}`}>
        {/* Radio options with improved responsive layout */}
        <div className='w-full'>
          <div className='flex flex-wrap items-center gap-2 sm:gap-3'>
            {availableOptions.map((option) => (
              <div
                key={option}
                className={`flex-1 min-w-[120px] flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
                  selectedOption === option
                    ? 'bg-yellow-50 border-yellow-200 shadow-sm'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => handleSelectOption(option)}
              >
                <CustomRadio size='small' checked={selectedOption === option} className='pointer-events-none' />
                <span
                  className={`text-sm font-medium whitespace-nowrap ${
                    selectedOption === option ? 'text-gray-800' : 'text-gray-400'
                  }`}
                >
                  {option}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Action controls container */}
        <div className='w-full'>
          <div className='w-full flex flex-col md:flex-row items-stretch gap-3'>
            {/* --- Time picker for "I'll Drive" --- */}
            {(selectedOption === CarpoolUpdate.UPDATE_TIME || selectedOption === CarpoolUpdate.I_WILL_DRIVE) && (
              <div className='flex-1'>
                <TimePicker
                  value={dayjs(selectedTime)}
                  onChange={handleSelectTime}
                  disabled={!selectedTime}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#e5e5e5',
                            borderRadius: '8px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#e5e5e5',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#e5e5e5',
                            borderWidth: '1px',
                          },
                        },
                        width: '100%',
                      },
                      inputProps: {
                        readOnly: true,
                        style: { fontSize: '0.875rem' },
                      },
                    },
                  }}
                />
              </div>
            )}

            {/* --- Driver selector for "Other Driver" --- */}
            {selectedOption === CarpoolUpdate.ASSIGN_DRIVER && (
              <div className='flex-1'>
                <SelectMobileComponent
                  data_testid='carpool_driver_testid'
                  isRequired={true}
                  placeholder='Select a driver'
                  searchable={true}
                  options={sortedUsers.map((user) => {
                    const distanceText =
                      user._distance !== Infinity
                        ? `${(user._distance * 0.000621371).toFixed(1)} miles away`
                        : 'Location not set'
                    return {
                      value: user.userId,
                      label: (
                        <div className='flex flex-col gap-0.5'>
                          <div className='flex flex-wrap items-center gap-1.5'>
                            <span className='text-[#1A1A1A] text-sm font-medium leading-tight'>{user.userName}</span>
                          </div>
                          <span
                            className={`${user._distance !== Infinity ? 'text-[#FF7900] text-sm' : 'text-[#666666] text-xs'} font-normal leading-tight mt-0.5`}
                          >
                            {distanceText}
                          </span>
                        </div>
                      ),
                      // This is used for search
                      searchText: `${user.userName} ${distanceText}`.toLowerCase(),
                    }
                  })}
                  value={selectedDriver?.user.userId || ''}
                  onChange={handleSelectDriver}
                  isError={!!selectedOptionMessage}
                />
              </div>
            )}

            {/* --- Apply Changes button --- */}
            <div className='w-full md:w-auto'>
              <ButtonMobileComponent
                text='Apply Changes'
                disabled={!isValid}
                className='w-full md:px-8 h-10'
                onClick={isValidStopDriving ? () => setIsShowStopDrivingModal(true) : handleSubmit}
              />
            </div>
            {isValidStopDriving && (
              <ModalConfirmComponent
                isShow={!!isShowStopDrivingModal}
                title='Stop Driving?'
                body='All passengers will be removed and notified. This action cannot be undone. Are you sure?'
                actionIcon={'redInfoIcon'}
                actionText={'Confirm'}
                onActionCancel={() => setIsShowStopDrivingModal(false)}
                onActionSubmit={handleSubmit}
                data_testid='stop_driving_testid'
              />
            )}
          </div>

          {/* --- Selected option message --- */}
          {selectedOptionMessage && (
            <div
              className='w-full'
              ref={(el) => {
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                }
              }}
            >
              <MsgPanelComponent {...selectedOptionMessage} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default CalendarUpdateComponent
